<template>
  <div class="filter-set wide property-id" :id="filterSetId()">
    <div class="filter-name" @click.stop="$emit('toggle')">
      <span class="filter-name__label">Kohdenumero</span>
      <i class="toggle-expander icon-chevron-down"></i>
    </div>
    <expander :expanded="expanderVisible">
      <div class="listing-id-input">
        <input
          :id="config.testing.FILTER_PROPERTY_ID_INPUT"
          maxlength="6"
          type="text"
          class="form-input hide-number-input-spinner"
          v-model="propertyId"
          v-on:keyup.enter="handleSearch"
          v-on:focus="inputFocus($event)"
          v-on:blur="inputBlur($event)"
        />
        <form-button
          :disabled="isButtonDisabled"
          :class="getButtonClass()"
          text="Hae"
          theme="primary"
          @click.native="handleSearch()"
        ></form-button>
        <div class="error-message" v-if="errorMsg">{{ errorMsg }}</div>
      </div>
    </expander>
  </div>
</template>

<script lang="ts">
import * as actions from '@/app/store/action-types';
import config from '@/config';
import Expander from '../expander/expander.vue';
import utils from '@/app/utils/utils';
import router from '../../router';
import { listing } from '@/app/routes';
import { computed, defineAsyncComponent, defineComponent, ref } from 'vue';
import useListingData from '@/app/data/useListingData';
import { isNotFoundError } from '@/utils/errors';

export interface Validation {
  valid: boolean;
  errorMsg: string;
}

export default defineComponent({
  name: 'property-id',
  setup() {
    const propertyId = ref<string>('');

    // Focus handling
    const gotFocus = ref<boolean | undefined>();
    const lostFocus = ref<boolean | undefined>();
    function inputFocus(event: FocusEvent) {
      gotFocus.value = true;
      lostFocus.value = false;
      utils.inputFocusOnAndroid(event);
    }
    function inputBlur(_: FocusEvent) {
      lostFocus.value = true;
      gotFocus.value = false;
    }

    // Validation
    const validation = computed<Validation>(() => {
      propertyId.value = propertyId.value.replace(/\D/g, '');
      if (!propertyId.value) {
        return { valid: false, errorMsg: '' };
      }
      if (propertyId.value?.length < 6) {
        return lostFocus.value
          ? { valid: false, errorMsg: 'Kohdenumeron tulee olla 6-numeroinen' }
          : { valid: false, errorMsg: '' };
      }
      return { valid: true, errorMsg: '' };
    });

    // Data fetching for listing id
    const { isError, error } = useListingData(
      propertyId,
      computed(() => validation.value.valid)
    );

    // Error message
    const errorMsg = computed(() => {
      if (validation.value.errorMsg) return validation.value.errorMsg;
      if (isNotFoundError(error.value)) return 'Kohdetta ei löytynyt. Tarkista kohdenumero.';
      return '';
    });

    const isButtonDisabled = computed(() => !validation.value.valid || isError.value);

    return { isError, errorMsg, propertyId, validation, inputFocus, inputBlur, isButtonDisabled };
  },
  data: () => ({
    config,
    id: `property-id-${utils.newId()}`,
  }),
  props: {
    expanderVisible: {
      type: Boolean,
      default: undefined, // By default, the expander is visible but without any complex close/open css logic
    },
  },
  methods: {
    handleSearch() {
      if (this.validation.valid && !this.isError) {
        void this.$store.dispatch(actions.analytics.set, { eVar55: this.propertyId });
        void this.$store.dispatch(actions.analytics.track, config.analytics.eventTypes.propertySearchbyID);
        void router.push(`${listing.path}/${this.propertyId}`);
      }
    },
    getButtonClass() {
      return this.validation.valid && !this.isError ? 'search' : 'search is-disabled';
    },
    filterSetId() {
      return this.id;
    },
  },
  components: {
    FormButton: defineAsyncComponent(() => import('@/app/components/form-button/form-button.vue')),
    Expander,
  },
});
</script>
